import React from 'react';
import Box from '@mui/material/Box';
import { Button, Tabs, Tab, LinearProgress, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import BugReportIcon from '@mui/icons-material/BugReport';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import VEVisualPreviewTab from './VEVisualPreviewTab';
import VEJSONEditorDialog from './VEJSONEditorDialog';
// import VEAlertDialog from './VEAlertDialog';
import { useAlert } from './AlertContext';

import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight as hljsStyle } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { useMovie } from "../movie/MovieProvider";
import { useMovieSetter } from "../movie/MovieProvider";


import J2VAuth from "../libs/auth.js";
import Defaults from '../libs/defaults';

export default function VEPreviewPanel(props) {

    const [activeTab, setActiveTab] = React.useState("preview");
    const [openJSONEditor, setOpenJSONEditor] = React.useState(false);
    const [userCredentials, setUserCredentials] = React.useState(J2VAuth.getCredentials());
    const [accountInfo, setAccountInfo] = React.useState(null);
    const [renderStatus, setRenderStatus] = React.useState(0);
    const [elapsedTime, setElapsedTime] = React.useState(0);
    const [renderMessage, setRenderMessage] = React.useState("");
    const [renderProgress, setRenderProgress] = React.useState(0);
    const [projectID, setProjectID] = React.useState(null);
    const [trackingInterval, setTrackingInterval] = React.useState(null);
    const trackingIntervalRef = React.useRef(trackingInterval);
    const [renderedMovieData, setRenderedMovieData] = React.useState(null);
    const [devAPI, setDevAPI] = React.useState(false);

    const movie = useMovie();
    const setMovie = useMovieSetter();

    const { showAlert } = useAlert();

    const selectedItemType = (movie.selected) ? movie.selected.type : "";

    const json = (movie.selected) ? JSON.stringify(movie.selected.pointer, null, 2) : "";

    React.useEffect(() => {

        const urlParams = new URLSearchParams(window.location.search);
        const devAPI = urlParams.get('dev_api');
        if (devAPI==="yes") {
            setDevAPI(true);
            Defaults.baseURL = "https://apidev.json2video.com/v1_1";
        }

        return () => {
            stopTrackingRender();
        };        
    }, []);

    React.useEffect(() => {
        if (activeTab === "render") {
            updateQuotaInfo();
        }
    }, [activeTab]);

    React.useEffect(() => {
        trackingIntervalRef.current = trackingInterval;
    }, [trackingInterval]);

    return (
        <Box className="w-5/12 m-1 flex-grow flex flex-col shrink">
            <Box className="border border-gray-200 flex flex-col flex-grow">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab value="preview" label="Preview" />
                        <Tab value="render" label="Render" />
                        <Tab value="json" label="JSON" />
                        <Tab value="php" label="PHP" />
                        <Tab value="nodejs" label="NodeJS" />
                    </Tabs>
                </Box>

                {
                    activeTab === "preview" &&
                    <VEVisualPreviewTab {...props} />
                }
                {
                    activeTab === "render" &&
                    showRenderTab()
                }
                {
                    activeTab === "json" &&
                    showJSONCode()
                }
                {
                    activeTab === "php" &&
                    showPHPCode()
                }
                {
                    activeTab === "nodejs" &&
                    showNodeJSCode()
                }

            </Box>
            {showRenderPanel()}
            <VEJSONEditorDialog json={json} openDialog={openJSONEditor} setOpenDialog={setOpenJSONEditor} updateHandler={updateJSONFromDialog} />
        </Box>
    );

    function handleTabChange(event, tab) {
        setActiveTab(tab);
    }

    function showJSONCode() {

        return (
            <Box className="h-0 flex-grow flex flex-col">
                <Box className="bg-white sticky top-0 border-b flex justify-between items-center">
                    <Box className="pl-3 font-bold text-sm">{selectedItemType.slice(0, 1).toUpperCase() + selectedItemType.slice(1).toLowerCase()} JSON</Box>
                    <Box>
                        <IconButton aria-label='Copy to clipboard' onClick={() => navigator.clipboard.writeText(json)}>
                            <ContentCopyIcon />
                        </IconButton>
                        {/* <IconButton aria-label='Edit JSON' onClick={() => setOpenJSONEditor(true)}>
                            <EditIcon />
                        </IconButton> */}
                    </Box>
                </Box>
                <Box className="px-3 py-2 h-0 flex-grow overflow-auto">
                    <SyntaxHighlighter language="json" style={hljsStyle}>
                        {json}
                    </SyntaxHighlighter>
                </Box>
            </Box>
        );
    }

    function showPHPCode() {
        const phpCode = window.translateJSONToSDK(json, "php", selectedItemType).trim();

        return (
            <Box className="h-0 flex-grow flex flex-col">
                <Box className="bg-white sticky top-0 border-b flex justify-between items-center">
                    <Box className="pl-3 font-bold text-sm">{selectedItemType.slice(0, 1).toUpperCase() + selectedItemType.slice(1).toLowerCase()} PHP Code</Box>
                    <Box>
                        <IconButton aria-label='Copy to clipboard' onClick={() => navigator.clipboard.writeText(phpCode)}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box className="px-3 py-2 h-0 flex-grow overflow-auto">
                    <SyntaxHighlighter language="php" style={hljsStyle}>
                        {phpCode}
                    </SyntaxHighlighter>
                </Box>
            </Box>
        )
    }

    function showNodeJSCode() {
        const nodejsCode = window.translateJSONToSDK(json, "javascript", selectedItemType).trim();

        return (
            <Box className="h-0 flex-grow flex flex-col">
                <Box className="bg-white sticky top-0 border-b flex justify-between items-center">
                    <Box className="pl-3 font-bold text-sm">{selectedItemType.slice(0, 1).toUpperCase() + selectedItemType.slice(1).toLowerCase()} PHP Code</Box>
                    <Box>
                        <IconButton aria-label='Copy to clipboard' onClick={() => navigator.clipboard.writeText(nodejsCode)}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box className="px-3 py-2 h-0 flex-grow overflow-auto">
                    <SyntaxHighlighter language="javascript" style={hljsStyle}>
                        {nodejsCode}
                    </SyntaxHighlighter>
                </Box>
            </Box>
        )
    }

    function showRenderTab() {

        let title = "";
        switch(renderStatus) {
            case 1: title="RENDER PROGRESS"; break;
            case 2: title="RENDER COMPLETE ✅"; break;
            case 3: title="RENDER FAILED ❌"; break;
            default: title="";
        }

        return (
            <Box className="m-5 h-0 flex-grow flex flex-col">
                {showPlanInfo()}

                <Box className="mt-8 w-full border-b font-bold text-xs pb-1">LAST RENDER DETAILS</Box>
                <Box className="px-5 mt-3">
                    <Box className="text-xs mb-4">
                        <span className="font-bold">STATUS</span>: &nbsp;
                        <span>{title}</span>
                    </Box>
                    <Box className="text-xs mb-4">
                        <span className="font-bold">PROJECT ID</span>: &nbsp;
                        <span>{projectID}</span>
                    </Box>
                    <Box className="text-xs mb-4">
                        <span className="font-bold">RENDERING TIME</span>: &nbsp;
                        <span>{Math.floor(elapsedTime/60)}:{("0" + Math.floor(elapsedTime%60)).slice(-2)}</span>
                        {
                            (renderStatus === 1) && <span>&nbsp; <CircularProgress size="0.5rem" /></span>
                        }
                    </Box>
                    <Box className="text-xs mb-4">
                        <span className="font-bold">RESPONSE</span>: &nbsp;
                        <span>{renderMessage}</span>
                    </Box>
                    {
                        (renderStatus === 2) &&
                        <>
                            <Box className="text-xs mb-4">
                                <span className="font-bold">VIDEO URL</span>: &nbsp;
                                <span><a href={renderedMovieData.url} target="_blank" rel="noopener noreferrer" className="underline">{renderedMovieData.url} <OpenInNewIcon fontSize="10px" /></a></span>
                            </Box>
                            <Box className="text-xs mb-4">
                                <span className="font-bold">VIDEO DURATION</span>: &nbsp;
                                <span>{renderedMovieData.duration} seconds</span>
                            </Box>
                            <Box className="text-xs mb-4">
                                <span className="font-bold">VIDEO RESOLUTION</span>: &nbsp;
                                <span>{renderedMovieData.width}px x {renderedMovieData.height}px</span>
                            </Box>
                            <Box className="text-xs mb-4">
                                <span className="font-bold">VIDEO FILE SIZE</span>: &nbsp;
                                <span>{Math.round((renderedMovieData.size/1024/1024)*100)/100} Mbytes</span>
                            </Box>
                        </>
                    }
                </Box>
            </Box>
        );
    }

    function showPlanInfo() {

        let remainingDrafts = 0, remainingMovies = 0, remainingTime = 0, remainingTimePC = 0, timeMeteredAccount = false;
        if (accountInfo) {
            if ("time" in accountInfo.remaining_quota) {
                remainingTime = accountInfo.remaining_quota.time;
                remainingTimePC = remainingTime/accountInfo.account.plan_info.time*100;
                timeMeteredAccount = true;
                console.log("remaining time: " + remainingTime);
            }
            else {
                remainingDrafts = accountInfo.remaining_quota.drafts/accountInfo.account.plan_info.drafts*100;
                remainingMovies = accountInfo.remaining_quota.movies/accountInfo.account.plan_info.movies*100;
                console.log("remaining drafts: " + remainingDrafts);
                console.log("remaining movies: " + remainingMovies);
            }
        }

        return (
            <Box className="">
                <Box className="w-full border-b font-bold text-xs pb-1">YOUR PLAN</Box>
                <Box className="px-5 mt-3 w-full flex justify-between text-xs">
                    {
                        (accountInfo) ?
                            <>
                                <span>PLAN: <span>{accountInfo.account.plan.toUpperCase()}</span></span>
                                {
                                    (timeMeteredAccount) ?
                                        <span>REMAINING TIME: {Math.floor(remainingTime/60)}:{("0" + Math.floor(remainingTime%60)).slice(-2)} <LinearProgress variant="determinate" value={remainingTimePC} className="w-32 mt-1" /></span>
                                        :
                                        <>
                                            <span>REMAINING DRAFTS: {accountInfo.remaining_quota.drafts} <LinearProgress variant="determinate" value={remainingDrafts} className="w-32 mt-1" /></span>
                                            <span>REMAINING MOVIES: {accountInfo.remaining_quota.movies} <LinearProgress variant="determinate" value={remainingMovies} className="w-32 mt-1" /></span>
                                        </>
                                }
                            </>
                            :
                            <Box className="w-full">
                                <LinearProgress />
                            </Box>
                    }
                </Box>
            </Box>
        );
    }

    function showRenderProgress() {

        let title = "";
        switch(renderStatus) {
            case 1: title="RENDER PROGRESS"; break;
            case 2: title="RENDER COMPLETE ✅"; break;
            case 3: title="RENDER FAILED ❌"; break;
            default: title="UNKNOWN ERROR ⚠️";
        }

        return (
            <>
                <Box className="flex justify-between">
                    <Box className="w-1/3 m-2 text-xs">{title}</Box>
                    <Box className="w-1/3 m-2 text-xs text-center">{Math.floor(elapsedTime/60)}:{("0" + Math.floor(elapsedTime%60)).slice(-2)}</Box>
                    {
                        (renderStatus === 1) && <Box className="w-1/3 text-right"><Button variant="text" size="small" onClick={cancelRender}>Cancel</Button></Box>
                    }
                    {
                        (renderStatus !== 1) && <Box className="w-1/3 text-right"><IconButton size="small" onClick={cancelRender}><CancelIcon fontSize="small" /></IconButton></Box>
                    }
                </Box>
                <Box className="mx-2">
                    <LinearProgress variant="determinate" value={renderProgress} />
                </Box>
                <Box className="flex justify-center items-center text-sm m-2">
                    { (renderStatus === 1) && <CircularProgress size="0.8rem" /> }
                    { (renderStatus !== 2) && <span className="ml-3">{renderMessage}</span> }
                    { (renderStatus === 2) && <Button variant="contained" size="small" onClick={openMovie}>Open</Button> }
                </Box>
            </>
        );
    }

    function cancelRender() {
        stopTrackingRender();
        setRenderStatus(0);
    }

    function openMovie() {
        window.open(renderedMovieData.url, "_blank");
        // setActiveTab("render");
    }

    function showRenderPanel() {

        if (renderStatus === 0) {
            const buttonColor = (!devAPI) ? "primary" : "secondary";
            return (
                <Box className="border border-gray-200 mt-2 h-20 flex items-center justify-center">
                    <Button variant="outlined" color={buttonColor} startIcon={
                        (devAPI) ? <BugReportIcon /> : <VideoSettingsIcon />
                    } onClick={renderMovie}>
                        Render movie {(devAPI) ? " with devAPI" : ""}
                    </Button>
                </Box>
            );
        }
        else {
            return (
                <Box className="border border-gray-200 mt-2 h-20">
                    {showRenderProgress()}
                </Box>
            );
        }
        
    }

    async function renderMovie() {
        // setup for start
        setElapsedTime(0);
        setRenderedMovieData(null);
        setRenderMessage("submitting movie...");
        setRenderProgress(0);
        setRenderStatus(1);

        if (!userCredentials) {
            setRenderStatus(0);
            stopTrackingRender();
            console.log("No user credentials!");

            showAlert({
                title: "Oops! You are not logged in!",
                message: [
                    "<strong>You need an account and an API key to render a movie</strong>.",
                    "Please, login or sign up to JSON2Video to render a movie."
                ],
                actions: [{
                    title: "Close",
                    variant: "contained"
                }, {
                    title: "Login",
                    variant: "outlined",
                    handler: () => {
                        window.open("/login?redirect=/tools/visual-editor/", "_blank").focus();
                    }
                }, {
                    title: "Sign Up",
                    variant: "outlined",
                    handler: () => {
                        window.open("/get-api-key/", "_blank").focus();
                    }
                }]
            });
            return;
        }

        // submit movie
        const movieJob = await submitMovie();
        if (!movieJob) {
            setProjectID(null);
            setRenderStatus(3);
            stopTrackingRender();
            console.log("Failed to submit movie!");
            return;
        }
    }

    async function submitMovie() {
        console.log("Submitting movie...");

        try {
            const rawResponse = await fetch(Defaults.baseURL + "/movies", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    [userCredentials.headerName]: userCredentials.headerValue
                },
                body: JSON.stringify(movie.movieObject, null, 2)
            });

            const response = await rawResponse.json();
            console.log("Movie submitted!", response);

            if (!response.success) {
                if (response.message) {
                    setRenderMessage(response.message);
                }
                console.log("Failed to submit movie!", response);
                return false;
            }

            setProjectID(response.project);
            setRenderMessage("preparing movie");
            startTrackingRender(response.project);
            setRenderProgress(10);
        }
        catch(e) {
            console.log(e);
            setRenderMessage("Execution error");
            return false;
        }

        return true;
    }

    function startTrackingRender(projectToTrack) {
        
        let tick = 1;
        const interval = setInterval(() => {
            setElapsedTime((oldValue) => oldValue + 1);
            // console.log("Tick", tick, tick%5 );
            if (tick%5===0) {
                console.log("Checking Render Status: ", tick, tick%5 );
                checkMovieRenderStatus(projectToTrack);
            }
            if (tick >= 600) {
                console.log("Render timeout");
                setRenderMessage("render timeout");
                setRenderStatus(3);
                clearInterval(interval);
                showAlert({
                    title: "Render timeout",
                    message: "The render process has timed out",
                    actions: [{
                        title: "Close",
                        variant: "contained"
                    }]
                });
                return;
            }
            tick++;
        }, 1000);

        setTrackingInterval(interval);

        return interval;
    }

    function stopTrackingRender() {
        console.log("Stopping tracking render...", trackingIntervalRef.current);
        if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
    }

    async function checkMovieRenderStatus(projectToTrack) {
        const url = `${Defaults.baseURL}/movies/?project=${projectToTrack}`;
        const rawResponse = await fetch(url, {
            method: "GET",
            headers: {
                [J2VAuth.getCredentials().headerName]: J2VAuth.getCredentials().headerValue
            }
        });
        const response = await rawResponse.json();

        if (response.success && response.movie) {
            if (response.movie.message) {
                setRenderMessage(response.movie.message);
            }
            if (!response.movie.status) {
                stopTrackingRender();
                setRenderStatus(3);
            }
            else if (response.movie.status === "error") {
                stopTrackingRender();
                setRenderStatus(3);
            }
            else if (response.movie.status === "done") {
                stopTrackingRender();
                setRenderProgress(100);
                setRenderMessage("done");
                setRenderedMovieData(response.movie);
                setRenderStatus(2);
            }
            else {
                switch(response.movie.message) {
                    case "downloading assets": setRenderProgress(25); break;
                    case "rendering scenes": setRenderProgress(50); break;
                    case "concatenating scenes": setRenderProgress(75); break;
                    case "exporting movie": setRenderProgress(85); break;
                    default: 
                }
            }
        }
        else {
            stopTrackingRender();
            setRenderMessage("Unknown error!");
            setRenderStatus(3);
            showAlert({
                title: "Error",
                message: "There was an error checking the render status",
                actions: [{
                    title: "Close",
                    variant: "contained"
                }]
            });
        }

        // console.log("Checking movie render status...", response);
    }

    async function updateQuotaInfo() {
        const userData = await J2VAuth.refresh();
        if (!userData) {
            J2VAuth.redirectToLoginScreen();
        }
        setAccountInfo(userData.data);
    }

    function updateJSONFromDialog(newJSON) {
        try {
            // VEMovieHelper.updateItem(props, newJSON);
            setMovie({
                type: "replaceItemById",
                item: newJSON
            });
        }
        catch(error) {
            console.log("JSON syntax error: ", error);
        }

    }
}